import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import { ButtonScaleViewOpts } from 'src/lib/ui/components/ButtonScaleView';
import { FeatureButton } from 'src/game/components/FeatureButton';
import { CircularProgressBar } from 'src/game/components/shared/CircularProgressBar';
import { DOWNSCALE_FACTOR, downscale } from 'src/conf/scale';
import { now, user } from 'src/StateObserver';
import {
  buddiesGetEvent,
  buddiesGetPartyScore,
  buddiesGetPartySize,
  buddiesGetRemainingTime,
  buddiesGetScoreByPuzzleAttempts,
  buddiesHasAnythingToCheck,
  buddiesIsAvailable,
  buddiesIsLocked,
  buddiesGetCurrentStatus,
  BuddiesHudStatus,
  buddiesIsEnabled,
} from 'src/replicant/buddies/buddies.getters';
import {
  buddiesMaxParty,
  buddiesMilestoneThresholds,
  buddiesSchedule,
  buddiesUnlockLevel,
} from 'src/replicant/buddies/buddies.ruleset';
import { animateJumpingPoints } from 'src/game/animation/jumpingPoints';
import { assertNever, clamp } from 'src/replicant/utils';
import { AssetGroup } from '@play-co/timestep-core';
import { spineLoader } from '@play-co/timestep-spine';
import sounds from 'src/lib/sounds';

const assets = {
  bg: 'assets/ui/buddies/hudButton/bg.png',
  barNormal: 'assets/ui/buddies/hudButton/bar_normal.png',
  barClaim: 'assets/ui/buddies/hudButton/bar_claim.png',
  progressBg: 'assets/ui/buddies/hudButton/progress_bg.png',
  progressFill: 'assets/ui/buddies/hudButton/progress_fill.png',
  strawberry: 'assets/ui/buddies/hudButton/strawberry.png',
  player: 'assets/ui/buddies/hudButton/player.png',
  spine: {
    loadMethod: spineLoader,
    url: 'assets/ui/buddies/hudButton/spine/hud',
  },
};

export class BuddiesButton extends FeatureButton {
  private progressBar: CircularProgressBar;
  private players: ImageScaleView[] = [];

  constructor(opts: ButtonScaleViewOpts) {
    super({
      width: 144,
      height: 160,
      pressedOffsetY: 5,
      labelPanel: {
        width: downscale(206),
        height: downscale(66),
        offsetY: -4,
        labelOffsetX: -1,
        labelOffsetY: -4,
        background: {
          image: assets.barNormal,
        },
      },
      lockedOverlay: {},
      onDown: () => sounds.playSound('ok'),
      ...opts,
    });

    this.animationStateMap = {
      main: {
        type: 'spine',
        assetGroup: new AssetGroup(Object.values(assets)),
        url: assets.spine.url,
        scale: DOWNSCALE_FACTOR,
        offsetX: 0,
        offsetY: 0,
        sequence: ['layer2_maingraphic'],
        onEnter: () => this.update(),
      },
    };

    // Static bg image
    new ImageScaleView({
      superview: this.container,
      width: downscale(216),
      height: downscale(221),
      scaleMethod: 'contain',
      image: assets.bg,
      x: this.style.width * 0.5,
      y: this.style.height * 0.5,
      centerOnOrigin: true,
      zIndex: -2,
    });

    // Circular progress bar showing party score toward grand prize
    this.progressBar = new CircularProgressBar({
      superview: this.container,
      width: downscale(192),
      height: downscale(192),
      x: this.style.width * 0.5,
      y: this.style.height * 0.5 - 3,
      r: -Math.PI * 0.75,
      centerOnOrigin: true,
      zIndex: -1,
      // This progress should not fill entirely, because the circle is partially hidden behind the label panel.
      // We will set the domain so that the progress can reach the grand prize threshold with ~3/4 of the circle filled.
      domain: [0, this.getMaxProgress() * 1.3],
    });

    this.progressBar.setFrameOpts({ visible: false });
    this.progressBar.updateFillBgOpts({
      image: assets.progressBg,
    });
    this.progressBar.updateFillOpts({
      image: assets.progressFill,
    });

    for (let i = 0; i < buddiesMaxParty; i++) {
      const player = new ImageScaleView({
        superview: this.container,
        image: assets.player,
        width: downscale(60),
        height: downscale(94),
        x: 80 - i * 10,
        y: 80,
        zIndex: 0.9 - i * 0.01,
        visible: false,
      });
      this.players.push(player);
    }

    this.setNotification({
      condition: () => buddiesHasAnythingToCheck(user(), now()),
      positioning: { align: 'right' },
    });
  }

  public override getElementsToApplyFilter(): View[] {
    const elements: View[] = [];
    if (this.labelPanel) elements.push(this.labelPanel);
    if (this.spine) elements.push(this.spine);
    return elements;
  }

  public update() {
    const status = buddiesGetCurrentStatus(user(), now());

    // Show locked state for sub-level-20 invited users
    if (buddiesIsLocked(user(), now())) {
      this.setAnimationState('main');
      this.lock();
      // Re-enable click events so the locked tap handler works
      this.updateOpts({ blockEvents: false });
      this.updateTime(status);
      this.show();
      return;
    }

    if (!buddiesIsAvailable(user(), now())) {
      this.hide();
      return;
    }

    if (status === 'inactive') {
      this.hide();
      return;
    }

    this.show();
    this.setAnimationState('main');
    this.updateTime(status);
    this.updateProgress();
    this.updatePlayers();
  }

  public async playProgressAnimation(puzzleAttempts: number) {
    // Skip if feature is not enabled for the user
    if (!buddiesIsEnabled(user())) return;

    // Skip animation if there's no active event
    if (!buddiesSchedule.getCurrentEvent(now())) return;

    const puzzleLevel = user().match2.level;

    // When reach the unlock level, play the unlock animation
    if (this.isLocked && puzzleLevel >= buddiesUnlockLevel) {
      await this.unlock();
    }

    // Skip jumping points while under minimum level.
    // Using -1 because user just played the previous one - state was already updated
    if (puzzleLevel - 1 < buddiesUnlockLevel) {
      return;
    }

    // Play the jumping points animation
    await animateJumpingPoints({
      button: this,
      // Buddies uses puzzleAttempts + 1 to match the action (losingStreak + 1)
      count: buddiesGetScoreByPuzzleAttempts(puzzleAttempts + 1),
      iconImage: assets.strawberry,
      iconSize: 80,
    });
  }

  private getMaxProgress() {
    return buddiesMilestoneThresholds[buddiesMilestoneThresholds.length - 1];
  }

  private updateTime(status: BuddiesHudStatus) {
    switch (status) {
      case 'inProgress':
        this.setLabelTime({
          duration: buddiesGetRemainingTime(now()),
        });
        this.updateLabelPanel({
          background: {
            image: assets.barNormal,
          },
        });
        break;
      case 'claim':
        this.setLabelLocaleText('match2.buddies.hudClaim');
        this.updateLabelPanel({
          background: {
            image: assets.barClaim,
          },
        });
        break;
      case 'inactive':
        this.setLabelLocaleText('match2.buddies.hudEnded');
        this.updateLabelPanel({
          background: {
            image: assets.barNormal,
          },
        });
        break;
      default:
        assertNever(status);
    }
  }

  private updateProgress() {
    const score = buddiesGetPartyScore(user(), now());
    const max = this.getMaxProgress();
    this.progressBar.progress = clamp(score, 0, max);
  }

  private updatePlayers() {
    const event = buddiesGetEvent(user(), now());
    const partySize = event ? buddiesGetPartySize(event) : 1;
    this.players.forEach((p, i) => p.updateOpts({ visible: i < partySize }));
  }
}
